import {
    ALL_LETTERS_BONUS,
    DICTIONARY_SET,
    GRID_LENGTH,
    GridSpotType,
    INITIAL_TYPE_GRID,
    TRAY_LENGTH
} from "./constants";
import {Letter, TileData} from "./Tile";

export interface Error {
    type: string;
    message: string;
}

export function coalesce<T>(a: T, b: T):T{
    if(a == null){
        return b;
    } else {
        return a;
    }

}

export function checkIfPresentAndAnchor(gridTiles: Array<TileData>, i: number, j:number){
    if(i < 0 || i >= GRID_LENGTH || j < 0 || j >= GRID_LENGTH){
        return [false, false];
    }

    const key = i*GRID_LENGTH + j;

    const tileData = gridTiles[key];
    if(tileData == null){
        return [false, false];
    } else if(tileData.isEphemeral){
        return [true, false];
    } else{
        return [true, true];
    }
}

// isAnchored means that a tile can be placed here (it's adjacent to a set tile)
// isConnected is similar, except that it also counts for 'ephemeral' tiles
export function checkTileForAnchorAndConnections(gridTiles: Array<TileData>, key: number, directions: Array<Direction> = [Direction.ROW, Direction.COLUMN]){
    const i = Math.floor(key / GRID_LENGTH);
    const j = key % GRID_LENGTH;

    let isConnected = false;
    let isAnchored = false;

    if(i==7 && j==7){
        // Middle tile
        isAnchored = true;
    }

    if(directions.includes(Direction.ROW)){
        for(let j_offset=-1; j_offset<=1; j_offset+=2) {
            const [isTileConnection, isTileAnchor] = checkIfPresentAndAnchor(gridTiles, i, j+j_offset);
            isConnected = isConnected || isTileConnection;
            isAnchored = isAnchored || isTileAnchor;
        }
    }

    if(directions.includes(Direction.COLUMN)){
        for(let i_offset=-1; i_offset<=1; i_offset+=2){
            const [isTileConnection, isTileAnchor] = checkIfPresentAndAnchor(gridTiles, i+i_offset, j);
            isConnected = isConnected || isTileConnection;
            isAnchored = isAnchored || isTileAnchor;
        }
    }

    return [isConnected, isAnchored];

}

// Direction means that the word is going alongside the specific direction (i.e. ROW means the word has one row, multiple columns)
export const enum Direction {
    ROW = 'ROW',
    COLUMN = 'COLUMN'
}

export function reverseDirection(direction: Direction):Direction{
    if(direction == Direction.ROW){
        return Direction.COLUMN;
    } else{
        return Direction.ROW;
    }
}

export function getKeyGenerator(direction: Direction, fixed_coordinate: number){
    if(direction == Direction.COLUMN){
        return (i) => GRID_LENGTH*i + fixed_coordinate;
    } else{
        return (i) => GRID_LENGTH*fixed_coordinate + i;
    }
}

export function findAllWords(tileData: Array<TileData>, direction: Direction, i: number, j: number):Array<Word>{
    const words: Array<Word> = [];

    // at i,j we know that there's an ephemeral tile;
    // we know that the ephemeral tiles have been placed in the specified direction
    // we need to go through and see all the words that the player has formed

    // Let's first see what word we are dealing with immediately
    let initialWord = findWordInDirection(tileData, direction, i, j);
    if(initialWord == null){
        // edge case - player only placed one tile and so direction is column, but the tile was placed at the end of
        // a word - thus there's a 'cross word' but not an initial word.
        if(direction == Direction.COLUMN){
            direction = Direction.ROW;
        } else {
            direction = Direction.COLUMN;
        }
        initialWord = findWordInDirection(tileData, direction, i, j);
    }


    words.push(initialWord);

    let fixed_coordinate;
    let crossDirection: Direction;
    if(direction == Direction.ROW){
        fixed_coordinate = i;
        crossDirection = Direction.COLUMN;
    } else{
        fixed_coordinate = j;
        crossDirection = Direction.ROW;
    }
    const keyFunction = getKeyGenerator(direction, fixed_coordinate);

    for(let offset=0; offset<initialWord.letters.length; offset++){
        const key = keyFunction(offset + initialWord.start);
        const tile = tileData[key];
        if(tile.isEphemeral){
            // we need to check the cross direction
            const new_i = Math.floor(key/GRID_LENGTH);
            const new_j = key % GRID_LENGTH;

            const potentialWord = findWordInDirection(tileData, crossDirection, new_i, new_j);
            if(potentialWord != null){
                words.push(potentialWord);
            }
        }

    }

    return words;
}

export function findWordInDirection(tileData: Array<TileData>, direction: Direction, i: number, j: number):Word{

    // at i,j we know that there's an ephemeral tile;
    // and we've been asked to see if there's a word in the specified direction

    let fixed_coordinate;
    let varying_coordinate;
    if(direction == Direction.ROW){
        fixed_coordinate = i;
        varying_coordinate = j;
    } else{
        fixed_coordinate = j;
        varying_coordinate = i;
    }
    const keyFunction = getKeyGenerator(direction, fixed_coordinate);

    // Check before and after the tile
    let startCoord = varying_coordinate-1;
    let endCoord = varying_coordinate+1;

    while(endCoord < GRID_LENGTH){
        const key = keyFunction(endCoord);
        const tile = tileData[key];

        if(tile == null){
            break;
        } else{
            endCoord++;
        }
    }
    endCoord--;

    while(startCoord >= 0){
        const key = keyFunction(startCoord);
        const tile = tileData[key];

        if(tile == null){
            break;
        } else{
            startCoord--;
        }
    }
    startCoord++;

    if(startCoord != endCoord){
        // There's a word! Let's retrieve it.
        const letters: Array<Letter> = [];
        for(let index=startCoord; index<= endCoord; index++){
            const key = keyFunction(index);
            letters.push(tileData[key].letter);
        }

        return new Word(direction, fixed_coordinate, startCoord, letters);
    }


    return null;
}

export class Word {
    direction: Direction;
    fixed_coordinate: number;
    start: number;
    letters: Array<Letter>;


    constructor(direction: Direction, fixed_coordinate: number, start: number, letters: Array<Letter>) {
        this.direction = direction;
        this.fixed_coordinate = fixed_coordinate;
        this.start = start;
        this.letters = letters;
    }

    getWordAsString():string{
        let word = "";

        for(let i=0; i<this.letters.length; i++){
            word = word + this.letters[i].str;
        }

        return word;
    }

    isValidWord():boolean{
        let word = this.getWordAsString();
        word = word.toUpperCase();

        return DICTIONARY_SET.has(word);
    }

    score(tileData: Array<TileData>):number{
        const keyFunction = getKeyGenerator(this.direction, this.fixed_coordinate);

        let wordMultiplier = 1;
        let score = 0;
        let countEphemeral = 0;

        for(let i=0; i<this.letters.length; i++){
            const key = keyFunction(i + this.start);
            const tile = tileData[key];

            let letterScore = tile.letter.points;
            if(tile.isEphemeral){
                countEphemeral++;
                const gridSpot = INITIAL_TYPE_GRID[key];
                if(gridSpot == GridSpotType.DOUBLE_LETTER){
                    letterScore *= 2;
                } else if(gridSpot == GridSpotType.TRIPLE_LETTER){
                    letterScore *= 3;
                } else if(gridSpot == GridSpotType.DOUBLE_WORD || gridSpot == GridSpotType.START){
                    wordMultiplier *= 2;
                } else if(gridSpot == GridSpotType.TRIPLE_WORD){
                    wordMultiplier *= 3;
                }
            }

            score += letterScore;
        }

        score = score * wordMultiplier;
        if(countEphemeral == TRAY_LENGTH){
            score += ALL_LETTERS_BONUS;
        }

        return score;

    }

}

export function scoreGrid(gridTileData: Array<TileData>):Array<WordScore> {
    let i_first = null;
    let j_first = null;
    let i_last = null;
    let j_last = null;
    let playedOnRow = false;
    let playedOnColumn = false;

    // Verify that all tiles are in either the same row or the same column
    {
        for (let i = 0; i < GRID_LENGTH; i++) {
            for (let j = 0; j < GRID_LENGTH; j++) {
                const key = i * GRID_LENGTH + j;
                const tileData = gridTileData[key];

                if (tileData != null && tileData.isEphemeral) {
                    i_last = i;
                    j_last = j;

                    if (i_first == null) {
                        // We've come to the start of what our player has played - all other played tiles will need to be either to the right or down from this one
                        i_first = i;
                        j_first = j;
                    } else {
                        // this is a new tile
                        if (i != i_first) {
                            playedOnColumn = true;
                        }
                        if (j != j_first) {
                            playedOnRow = true;
                        }

                        if (playedOnRow && playedOnColumn) {
                            // This is an invalid move
                            throw {
                                message: "Detected player tiles on both different rows & columns",
                                type: "WordMisplacedError",
                            };
                        }
                    }

                    // Finally let's just do a quick check on whether it's a blank tile and it has a letter or not
                    if(tileData.letter.is_blank && tileData.letter.str.length != 1){
                        throw {
                            message: "Invalid move - you need to fill in any blank tiles first",
                            type: "WordMisplacedError",
                        };
                    }
                }
            }
        }
    }

    if(i_first == null){
        throw {
            message: "No played tiles detected",
            type: "NoPlayerTilesError",
        };
    }

    let playedDirection: Direction;
    let keyCrafterFn: Function;
    let first_index;
    let last_index;
    if (playedOnRow) {
        playedDirection = Direction.ROW;
        keyCrafterFn = getKeyGenerator(playedDirection, i_first);
        first_index = j_first;
        last_index = j_last;
    } else {
        // Note that this also includes the case of only one tile played
        playedDirection = Direction.COLUMN;
        keyCrafterFn = getKeyGenerator(playedDirection, j_first);
        first_index = i_first;
        last_index = i_last;
    }

    // Verify that played tiles are in contact with existing tiles
    {
        let allTilesConnected = true;
        let wordFixed = false;
        for (let index = 0; index < GRID_LENGTH; index++) {
            const key = keyCrafterFn(index);
            const tileData = gridTileData[key];
            if (tileData != null && tileData.isEphemeral) {
                const [tileConnected, tileAnchored] = checkTileForAnchorAndConnections(gridTileData, key);
                allTilesConnected = allTilesConnected && tileConnected;
                wordFixed = wordFixed || tileAnchored;
            }
        }

        if (!allTilesConnected || !wordFixed) {
            throw {
                message: "Some tiles are not adjacent to other tiles and/or word is not touching fixed tiles",
                type: "WordMisplacedError",
                details: {
                    allTilesConnected: allTilesConnected,
                    wordFixed: wordFixed,
                }
            };
        }
    }

    // Verify that there are no gaps in the played row/column
    {
        for(let index=first_index; index<=last_index; index++){
            const key = keyCrafterFn(index);
            const tileData = gridTileData[key];
            if(tileData == null){
                throw {
                    message: "Gap between tiles detected",
                    type: "WordGapError",
                };
            }
        }
    }

    const words = findAllWords(gridTileData, playedDirection, i_first, j_first);
    let scores: Array<WordScore> = [];

    for(let i=0; i<words.length; i++){
        const word = words[i];
        const wordAsText = word.getWordAsString();
        if(word.isValidWord()){
            scores.push({
                word: wordAsText,
                score: word.score(gridTileData),
            });
        } else{
            throw {
                message: `${wordAsText} is not a valid word`,
                type: "InvalidWord",
                word: wordAsText,
            };
        }

    }

    return scores;

}

export interface WordScore {
    word: string;
    score: number;
}

export class CountSet<T> {
    map: Map<T, number>;

    constructor(existingMap?: CountSet<T>) {
        if(existingMap == undefined){
            this.map = new Map<T, number>();
        } else{
            this.map = new Map<T, number>(existingMap.map);
        }
    }

    getCount(key: T): number{
        let existing = this.map.get(key);
        if(existing == undefined){
            existing = 0;
        }
        return existing;
    }

    increment(key: T){
        const existing = this.getCount(key);
        this.map.set(key, existing+1);
    }

    decrement(key: T){
        const existing = this.getCount(key);
        this.map.set(key, existing-1);
    }

}

export function randomizeArray<T>(array: Array<T>){
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}